import { useCallback } from 'react'

import type { Map } from 'immutable'
import { useSelector } from 'react-redux'

import type { RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketPostcode, getBasketRecipes, getNumPortions } from 'selectors/basket'
import {
  type BasketRecipe,
  getBasketRecipeNumPortions,
  getBasketRecipeQuantity,
} from 'utils/basket'

import { sumQuantities, useRecipeLimitReached } from '../limits'
import { useAddRecipeByUuid } from './useAddRecipe'
import { useRemoveRecipeByUuid } from './useRemoveRecipe'
import { useUpdateRecipePortionsByUuid } from './useUpdateRecipePortions'

/**
 * Can recipes be added to the basket at this point?
 *
 * Requires a postcode to be set
 *
 * @returns
 */
const useCanAddRecipes = () => {
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const postcode = useSelector(getBasketPostcode)

  if (hasProspectNotSeenWizard) return true

  return Boolean(postcode)
}

/**
 * Get the recipes currently in the basket
 *
 * @returns a map where the key is the recipe ID and the value is the recipe basket entry
 */
const useBasketRecipeEntries = () =>
  useSelector(getBasketRecipes) as unknown as Map<RecipeCoreId, BasketRecipe>

const useBasketRecipesByUuid = (): Record<RecipeUUID, unknown> => {
  return useSelector(getBasketRecipes).toJS()
}

/**
 * Entrypoint for the `basket recipes` information
 *
 * @returns addRecipe - add a recipe to the basket
 * @returns removeRecipe - remove a recipe from the basket
 * @returns swapRecipe - replace a recipe in the basket
 *
 */
export const useBasketRecipes = () => {
  const basketRecipeEntries = useBasketRecipeEntries()
  const basketRecipesByUuid = useBasketRecipesByUuid()
  const numPortions = useSelector(getNumPortions)

  const getQuantityForRecipeId = useCallback(
    (recipeId: RecipeUUID) =>
      getBasketRecipeQuantity(basketRecipesByUuid[recipeId] as BasketRecipe | undefined),
    [basketRecipesByUuid],
  )
  const getNumPortionsForRecipeId = useCallback(
    (recipeId: RecipeUUID) =>
      getBasketRecipeNumPortions(
        basketRecipesByUuid[recipeId] as BasketRecipe | undefined,
        numPortions,
      ),
    [basketRecipesByUuid, numPortions],
  )

  const addRecipeByUuid = useAddRecipeByUuid()
  const removeRecipeByUuid = useRemoveRecipeByUuid()
  const updateRecipePortionsByUuid = useUpdateRecipePortionsByUuid()
  const reachedLimit = useRecipeLimitReached(basketRecipeEntries)
  const canAddRecipes = useCanAddRecipes()

  const recipeCount = sumQuantities(basketRecipeEntries)

  return {
    addRecipeByUuid,
    removeRecipeByUuid,
    updateRecipePortionsByUuid,
    canAddRecipes,
    reachedLimit,
    recipeCount,
    getQuantitiesForRecipeId: getQuantityForRecipeId,
    getNumPortionsForRecipeId,
  }
}
